import React, { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowRightIcon, CalendarIcon, ClockIcon } from 'lucide-react';
import { getArticleBySlug, getRelatedArticles } from '../data/articles';
import { ArticleBody } from '../components/article/ArticleBody';
import { ArticleCTABand } from '../components/article/ArticleCTABand';
import { ArticleFAQ } from '../components/article/ArticleFAQ';
import { ArticleTOC } from '../components/article/ArticleTOC';
import { usePageMeta } from '../hooks/usePageMeta';
import { buildMatchLink } from '../utils/matchParams';
const FAQ_ID = 'faqs';
export function ArticlePage() {
  const {
    articleName
  } = useParams<{
    articleName: string;
  }>();
  const location = useLocation();
  // Each article also has an explicit route so it appears as its own URL, and
  // those routes carry no `:articleName` param — fall back to the path.
  const slug = articleName || location.pathname.split('/').filter(Boolean).pop() || '';
  const article = getArticleBySlug(slug);
  usePageMeta(article?.metaTitle || 'Article | Online College Guide', article?.metaDescription || 'Compare accredited online degree programs. Get matched free by career goals, schedule, and budget.', {
    ogType: 'article'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!article) {
    return <main className="min-h-[60vh] flex items-center justify-center px-4 py-20" data-id="element-934" data-mp="2hu7ge28gjyg1">
        <div className="text-center max-w-md" data-id="element-935" data-mp="1u6pidoylo44m">
          <h1 className="text-2xl font-bold text-navy mb-3" data-id="element-936" data-mp="1p7wfeqgxvvip">
            Article Not Found
          </h1>
          <p className="text-gray-600 mb-6" data-id="element-937" data-mp="22z17n7hz9lj0">
            We couldn't find that article. Browse our guides or get matched with
            accredited programs instead.
          </p>
          <Link to="/request-information" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-dark text-white font-bold py-3 px-7 rounded-lg text-sm transition-colors duration-200" data-id="element-938" data-mp="1ai8g1usbj6xm">
            Get Matched Free
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-939" data-mp="35ld6bxupdtbz" />
          </Link>
        </div>
      </main>;
  }
  const related = getRelatedArticles(article.slug);
  const midpoint = Math.ceil(article.sections.length / 2);
  const matchHref = buildMatchLink('/request-information', article.match);
  return <main className="bg-white" data-id="element-940" data-mp="3kcm4ivco8n0q">
      {/* Hero */}
      <header className="hero-bg" data-id="element-941" data-mp="2mnu5vimb9j16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-942" data-mp="2mols42u0q4es">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-943" data-mp="2rmds7k4tgwu2">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-944" data-mp="2wkfaw3ybyu50">
              <li data-id="element-945" data-mp="31k1oiei2mcec">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-946" data-mp="31kkxv6tnnsgr">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-947" data-mp="bug5o28o6z63">/</li>
              <li data-id="element-948" data-mp="gtkf8tziaywy">
                <Link to="/articles" className="hover:text-white transition-colors" data-id="element-949" data-mp="gt15w1nx9iuj">
                  Articles
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-950" data-mp="1wdtczv03sdk0">/</li>
              <li className="text-white/90" data-id="element-951" data-mp="1weuzqwqheohk">{article.category}</li>
            </ol>
          </nav>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-gold mb-4" data-id="element-952" data-mp="1rjyv8x4268nk">
            {article.eyebrow}
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-4xl" data-id="element-953" data-mp="1hodpqozs6q92">
            {article.title}
          </h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-3xl mb-6" data-id="element-954" data-mp="1dkz8xw40q5uk">
            {article.intro}
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/60" data-id="element-955" data-mp="18p0nohdzl3yd">
            <span className="inline-flex items-center gap-1.5" data-id="element-956" data-mp="13qfq6pq4cydm">
              <CalendarIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-957" data-mp="330ow00ppq89s" />
              Updated {article.updated}
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-958" data-mp="2y0az9etkakr2">
              <ClockIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-959" data-mp="1vq0uq0275uak" />
              {article.readMinutes} min read
            </span>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-960" data-mp="1lpqkmobgl2zm">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-961" data-mp="1qotow6jm756j">
          <article className="lg:w-2/3 space-y-12" data-id="element-962" data-mp="1qrfxm9i8rqqc">
            <ArticleBody sections={article.sections} interruptAfter={midpoint} interruption={<ArticleCTABand variant="inline" body={article.cta.body} buttonLabel={article.cta.buttonLabel} match={article.match} data-id="element-964" data-mp="1gt9w3t0ckmm0" />} data-id="element-963" data-mp="1bzx89l3hsjub" />
            <ArticleFAQ id={FAQ_ID} faqs={article.faqs} data-id="element-965" data-mp="t2w1pvuaibo9" />
          </article>

          {/* Sidebar */}
          <aside className="lg:w-1/3 space-y-8" data-id="element-966" data-mp="xzw8k8yyrigf">
            <div className="lg:sticky lg:top-24 space-y-8" data-id="element-967" data-mp="y2s5pv2zm33t">
              <div className="bg-navy rounded-xl p-6" data-id="element-968" data-mp="efxfqge7c9gx">
                <h2 className="text-lg font-bold text-white mb-2" data-id="element-969" data-mp="adtzlegl8gpb">
                  Get Matched Free
                </h2>
                <p className="text-sm text-white/75 leading-relaxed mb-5" data-id="element-970" data-mp="oed4icprb6n9">
                  Answer a few questions and we'll connect you with accredited
                  programs that fit your goals, schedule, and budget.
                </p>
                <Link to={matchHref} className="block text-center bg-cta hover:bg-cta-dark text-white font-bold py-3 px-5 rounded-lg text-sm transition-colors duration-200" data-id="element-971" data-mp="xbx5dkjpj0xc">
                  {article.cta.buttonLabel}
                </Link>
                <p className="mt-3 text-xs text-white/55 text-center" data-id="element-972" data-mp="126tc7qtlrsui">
                  Free for students. No obligation.
                </p>
              </div>

              <ArticleTOC sections={article.sections} faqId={FAQ_ID} data-id="element-973" data-mp="1c3z0yseqv7rk" />

              <div className="border-t border-gray-200 pt-6" data-id="element-974" data-mp="1j6e1f958bos4">
                <h2 className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-4" data-id="element-975" data-mp="1j419q49o601v">
                  Keep Reading
                </h2>
                <ul className="space-y-4" data-id="element-976" data-mp="1t40h3xucrkd5">
                  {article.internalLinks.map(link => <li key={link.anchor} data-id="element-977" data-mp="1xva9h67ad1sq">
                      <Link to={buildMatchLink(link.to, link.match ?? article.match)} className="group flex items-start gap-2 text-sm text-gray-700 hover:text-navy transition-colors duration-150" data-id="element-978" data-mp="1xx3dseuff9xk">
                        <ArrowRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400 group-hover:text-navy transition-colors duration-150" aria-hidden="true" data-id="element-979" data-mp="2gu4l46bgdm4o" />
                        <span className="capitalize" data-id="element-980" data-mp="mg3l3i462t30">{link.anchor}</span>
                      </Link>
                    </li>)}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Related articles */}
      {related.length > 0 && <section aria-labelledby="related-heading" className="bg-light border-t border-gray-200 py-14" data-id="element-981" data-mp="54eyt5z80vzx">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-982" data-mp="xvq7h4n63gtw">
            <h2 id="related-heading" className="text-2xl font-bold text-navy mb-6" data-id="element-983" data-mp="xsu9jgbbks5g">
              Related Guides
            </h2>
            <ul className="grid sm:grid-cols-3 gap-5" data-id="element-984" data-mp="nvfsbehknzc3">
              {related.map(item => <li key={item.slug} className="flex" data-id="element-985" data-mp="ixeasxzso82q">
                  <Link to={`/article/${item.slug}`} className="group bg-white border border-gray-200 rounded-xl p-6 flex flex-col w-full hover:border-navy transition-colors duration-200" data-id="element-986" data-mp="38mzvlfsr9vdm">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2" data-id="element-987" data-mp="3tapn8rszklv6">
                      {item.category}
                    </span>
                    <span className="font-bold text-gray-900 leading-snug mb-3" data-id="element-988" data-mp="3kcnpqm8gobcn">
                      {item.title}
                    </span>
                    <span className="mt-auto inline-flex items-center text-sm font-semibold text-navy" data-id="element-989" data-mp="3ofaak36psw0c">
                      Read guide
                      <ArrowRightIcon className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform duration-200" aria-hidden="true" data-id="element-990" data-mp="kx0ghbj8j3mb" />
                    </span>
                  </Link>
                </li>)}
            </ul>
          </div>
        </section>}

      <ArticleCTABand body={article.cta.body} buttonLabel={article.cta.buttonLabel} match={article.match} data-id="element-991" data-mp="yu5m5af1e0h1" />
    </main>;
}